/**
 * Master Data React Query Hooks
 * 
 * React Query hooks for fetching and caching master data
 */

import { useQuery } from '@tanstack/react-query';
import {
  masterDataService,
  type MasterDataLocationParams,
  type LivenessConfig,
  type FamilySizeAllowedConfig,
} from './master-data.service';
import type { FieldOption } from '../types/form-template.types';

// ============================================
// Query Keys
// ============================================

export const masterDataKeys = {
  all: ['master-data'] as const,
  application: () => [...masterDataKeys.all, 'application'] as const,
};

// ============================================
// Master Data Hooks
// ============================================

/**
 * Fetch master data for application forms
 * GET /v1/api/reference-data
 *
 * @param sources - Optional comma-separated list of sources to filter (e.g., "appointment_for")
 * @param enabled - Whether the query should run (default: true)
 * @param locationParams - Optional location context (countryId, entityId, entityType) for source-scoped requests
 */
export const useMasterData = (
  sources?: string,
  enabled: boolean = true,
  locationParams?: MasterDataLocationParams | null
) => {
  return useQuery<Record<string, FieldOption[]>, Error>({
    queryKey: [
      ...masterDataKeys.application(),
      sources || 'all',
      locationParams
        ? [locationParams.countryId, locationParams.entityId, locationParams.entityType]
        : [],
    ],
    queryFn: () => masterDataService.getMasterData(sources, locationParams ?? undefined),
    enabled,
    retry: 2,
  });
};

/**
 * Get options for a specific master data key
 * 
 * @param key - Master data key (e.g., 'applicant_gender', 'applicant_marital_status')
 * @param enabled - Whether the query should run (default: true)
 */
export const useMasterDataOptions = (key: string, enabled: boolean = true) => {
  const { data, ...rest } = useMasterData(undefined, enabled);

  return {
    ...rest,
    options: data?.[key] || [],
  };
};

/**
 * Fetch family booking limits from reference-data (primary's location context).
 * GET /v1/reference-data?sources=family_size_allowed&countryId=&entityId=&entityType=
 * Response includes `family_size_allowed` (number) and `family_multiple_passports_per_slot` (boolean).
 * Defaults for missing API values are applied in `getFamilySizeAllowedConfig`.
 */
export const useFamilySizeAllowed = (
  locationParams: MasterDataLocationParams | null,
  enabled: boolean = true
) => {
  return useQuery<FamilySizeAllowedConfig, Error>({
    queryKey: [...masterDataKeys.application(), 'family_size_allowed', locationParams ?? []],
    queryFn: () => masterDataService.getFamilySizeAllowedConfig(locationParams!),
    enabled: enabled && locationParams != null,
    retry: 2,
  });
};

export type AppointmentForChildConfigEntry = {
  visaTypeId: number;
  visaSubTypeId: number;
};

/**
 * Fetch appointment-for-child config (visa type + sub type pairs where child appointment is enabled for the center).
 * When visa is in config, all participants count for slots; when not, only participants with age > 6 count.
 * GET /v1/reference-data?sources=appointment_for_child_config&countryId=&entityId=&entityType=
 * Returns [] when disabled, loading, or on error.
 */
export const useAppointmentForChildConfig = (
  locationParams: MasterDataLocationParams | null,
  enabled: boolean = true
) => {
  return useQuery<AppointmentForChildConfigEntry[], Error>({
    queryKey: [...masterDataKeys.application(), 'appointment_for_child_config', locationParams ?? []],
    queryFn: () => masterDataService.getAppointmentForChildConfig(locationParams!),
    enabled: enabled && locationParams != null,
    retry: 2,
    placeholderData: [],
  });
};

/**
 * Fetch liveness detection config for the booking flow.
 * GET /v1/reference-data?sources=liveness_detection&countryId=
 * Used to show/hide liveness step and SKIP CTA when not mandatory.
 */
export const useLivenessConfig = (countryId: number, enabled: boolean = true) => {
  const isEnabled = enabled && Number.isFinite(countryId);

  return useQuery<LivenessConfig, Error>({
    queryKey: [...masterDataKeys.application(), 'liveness_detection', countryId],
    queryFn: () => masterDataService.getLivenessConfig(countryId),
    enabled: isEnabled,
    retry: 2,
  });
};

// ============================================
// VAS Safe Mode Config
// ============================================

export interface VasSafeModeConfig {
  enableSafeModeForVas: boolean;
}

/**
 * Fetch VAS safe mode config for a center.
 * GET /v1/reference-data?sources=vas_safe_mode_config&countryId=&entityId=&entityType=
 *
 * Returns `true` when safe mode is active and the SSE stream should be connected,
 * `false` otherwise. Defaults to `false` on error or missing data.
 */
export const useVasSafeModeConfig = (
  locationParams: MasterDataLocationParams | null,
) => {
  return useQuery<boolean, Error>({
    queryKey: [...masterDataKeys.all, 'vas-safe-mode-config', locationParams ?? []],
    queryFn: async () => {
      const data = await masterDataService.getMasterData('vas_safe_mode_config', locationParams!);
      const config = (data as Record<string, any>)?.vas_safe_mode_config as VasSafeModeConfig | undefined;
      return config?.enableSafeModeForVas ?? false;
    },
    enabled: !!locationParams,
    retry: 1,
  });
};

export type { FamilySizeAllowedConfig };
